import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

type Variant = 'default' | 'primary' | 'ghost' | 'header' | 'danger';
type Size = 'sm' | 'md' | 'lg';

function classes(variant: Variant, size: Size, extra?: string) {
  const v =
    variant === 'primary' ? 'pn-btn-primary'
    : variant === 'ghost' ? 'pn-btn-ghost'
    : variant === 'header' ? 'pn-btn-header'
    : variant === 'danger' ? 'pn-btn-danger'
    : '';
  const s = size === 'md' ? 'pn-btn-md' : size === 'lg' ? 'pn-btn-lg' : '';
  return ['pn-btn', v, s, extra].filter(Boolean).join(' ');
}

type CommonProps = { variant?: Variant; size?: Size; className?: string; children: ReactNode };

export function Button({
  variant = 'default',
  size = 'sm',
  className,
  children,
  ...rest
}: CommonProps & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button className={classes(variant, size, className)} {...rest}>
      {children}
    </button>
  );
}

export function ButtonLink({
  variant = 'default',
  size = 'sm',
  className,
  children,
  ...rest
}: CommonProps & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a className={classes(variant, size, className)} {...rest}>
      {children}
    </a>
  );
}

export function ButtonRouterLink({
  variant = 'default',
  size = 'sm',
  className,
  children,
  ...rest
}: CommonProps & LinkProps) {
  return (
    <Link className={classes(variant, size, className)} {...rest}>
      {children}
    </Link>
  );
}

/** Segmented button row — shared inner edges. Prefer flex `gap` for spaced clusters. */
export function ButtonGroup({
  className,
  children
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={['pn-btn-group', className].filter(Boolean).join(' ')} role="group">
      {children}
    </div>
  );
}
